import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { Subscription, interval } from 'rxjs';
import { ApiService } from '../../../../core/services/api.service';
import { BreakpointService } from '../../../../core/services/breakpoint.service';
import { ChatSocketService } from '../../../../core/services/chatSocketService';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { CallTranscriptionComponent } from '../../../private/call-history/call-transcription/call-transcription.component';
import {
  OngoingCallListItem,
  OngoingCallDetailView,
  FormattedTranscriptLine,
  extractApiArray,
  mapOngoingCallListItem,
  mapOngoingCallDetail,
  formatDurationFromStart,
  getSentimentClasses,
  ONGOING_CALLS_REFRESH_MS,
  matchesOngoingCall,
  extractSocketCallIds,
  mapSocketTranscriptLine,
  applySocketTranscriptUpdate,
  dedupeOngoingCalls,
  mapSocketSummaryUpdate,
  transcriptLinesFromSegments,
  seedTranscriptSegments,
  unwrapSocketPayload,
  pickString,
} from '../crm-panel.utils';

type LiveContextTab = 'cheatsheet' | 'salesPitch' | 'summary';
type MobileDetailTab = 'transcript' | 'context';

@Component({
  selector: 'app-live-monitoring',
  standalone: true,
  imports: [CommonModule, MarkdownModule, IconComponentV2, CallTranscriptionComponent],
  providers: [provideMarkdown()],
  templateUrl: './live-monitoring.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host ::ng-deep .insight-markdown p,
      :host ::ng-deep .insight-markdown li {
        line-height: 1.65;
      }
    `,
  ],
})
export class LiveMonitoringComponent implements OnInit, OnDestroy {
  private apiService = inject(ApiService);
  private chatSocketService = inject(ChatSocketService);
  private breakpointService = inject(BreakpointService);

  isMobile = this.breakpointService.isMobile;

  @Input() productPid: number | null = null;
  @Output() ongoingCountChange = new EventEmitter<number>();

  ongoingCalls = signal<OngoingCallListItem[]>([]);
  selectedCallKey = signal<string | null>(null);
  callDetail = signal<OngoingCallDetailView | null>(null);

  isListLoading = signal(true);
  isDetailLoading = signal(false);
  listError = signal<string | null>(null);
  detailError = signal<string | null>(null);
  isListRefreshing = signal(false);

  activeContextTab = signal<LiveContextTab>('cheatsheet');
  mobileShowCallDetail = signal(false);
  mobileDetailTab = signal<MobileDetailTab>('transcript');
  expandedFaqIndex = signal<number | null>(0);
  durationTick = signal(0);

  private listPollSub: Subscription | null = null;
  private durationSub: Subscription | null = null;
  private lastProductPid: number | null | undefined = undefined;
  private transcriptSegments = new Map<string, FormattedTranscriptLine>();

  selectedCall = computed(() => {
    const key = this.selectedCallKey();
    if (!key) return null;
    return this.ongoingCalls().find((c) => this.callKey(c) === key) ?? null;
  });

  callDurations = computed(() => {
    this.durationTick();
    const map = new Map<string, string>();
    for (const call of this.ongoingCalls()) {
      map.set(this.callKey(call), formatDurationFromStart(call.startTime));
    }
    return map;
  });

  selectedDuration = computed(() => {
    this.durationTick();
    const call = this.selectedCall();
    return call ? formatDurationFromStart(call.startTime) : '00:00';
  });

  constructor() {
    effect(() => {
      const pid = this.productPid;
      if (this.lastProductPid === undefined) return;
      if (pid === this.lastProductPid) return;
      this.lastProductPid = pid;
      this.selectedCallKey.set(null);
      this.callDetail.set(null);
      this.chatSocketService.clearVocalTransEngCallRoom();
      this.chatSocketService.joinVocalTransEngMonitoring(pid);
      this.restartListPolling();
      this.fetchOngoingCalls(true);
    });
  }

  ngOnInit(): void {
    this.lastProductPid = this.productPid;
    this.startDurationTimer();
    this.fetchOngoingCalls(true);
    this.startListPolling();
    this.setupSocketListeners();
  }

  ngOnDestroy(): void {
    this.stopListPolling();
    this.durationSub?.unsubscribe();
    this.chatSocketService.detachVocalTransEngListeners();
  }

  callKey(call: OngoingCallListItem): string {
    return `${call.conversationPid}-${call.traceId}`;
  }

  selectMobileDetailTab(tab: MobileDetailTab): void {
    this.mobileDetailTab.set(tab);
  }

  backToCallList(): void {
    this.mobileShowCallDetail.set(false);
  }

  isMobileTranscriptVisible(): boolean {
    if (!this.isMobile()) return true;
    return this.mobileShowCallDetail() && this.mobileDetailTab() === 'transcript';
  }

  isMobileContextVisible(): boolean {
    if (!this.isMobile()) return true;
    return this.mobileShowCallDetail() && this.mobileDetailTab() === 'context';
  }

  selectCallFromList(call: OngoingCallListItem): void {
    const isSameCall = this.selectedCallKey() === this.callKey(call);
    if (!isSameCall) {
      this.applyCallSelection(call);
    }
    if (this.isMobile()) {
      this.mobileDetailTab.set('transcript');
      this.mobileShowCallDetail.set(true);
    }
  }

  selectCall(call: OngoingCallListItem): void {
    this.selectCallFromList(call);
  }

  private applyCallSelection(call: OngoingCallListItem): void {
    const key = this.callKey(call);
    if (this.selectedCallKey() === key) return;
    console.debug('[LiveMonitoring] selectCall', {
      key,
      conversationPid: call.conversationPid,
      traceId: call.traceId,
      conversationId: call.conversationId,
    });
    this.selectedCallKey.set(key);
    this.detailError.set(null);
    this.transcriptSegments.clear();
    this.chatSocketService.joinVocalTransEngCallRoom(call.conversationPid, call.traceId);
    this.fetchCallDetail(true);
  }

  refreshOngoingCalls(): void {
    if (this.isListLoading() || this.isListRefreshing()) return;
    this.restartListPolling();
    this.fetchOngoingCalls(false);
  }

  selectContextTab(tab: LiveContextTab): void {
    this.activeContextTab.set(tab);
  }

  toggleFaq(index: number): void {
    this.expandedFaqIndex.set(this.expandedFaqIndex() === index ? null : index);
  }

  sentimentClass(sentiment: string): string {
    return getSentimentClasses(sentiment);
  }

  retryList(): void {
    this.restartListPolling();
    this.fetchOngoingCalls(true);
  }

  retryDetail(): void {
    this.fetchCallDetail(true);
  }

  private buildListParams(): Record<string, number> | undefined {
    if (this.productPid == null) return undefined;
    return { product_pid: this.productPid };
  }

  private fetchOngoingCalls(isInitial = false): void {
    if (isInitial) {
      this.isListLoading.set(true);
    } else {
      this.isListRefreshing.set(true);
    }
    this.listError.set(null);

    this.apiService
      .get<unknown>('/vocal-trans-eng/ongoing-calls', this.buildListParams(), {
        showLoader: false,
      })
      .subscribe({
        next: (res) => {
          const selectionBefore = this.selectedCallKey();
          const mapped = dedupeOngoingCalls(
            extractApiArray(res)
              .map(mapOngoingCallListItem)
              .filter((item): item is OngoingCallListItem => item != null)
          );

          this.ongoingCalls.set(mapped);
          this.ongoingCountChange.emit(mapped.length);
          this.syncSelectionAfterListUpdate(mapped);

          if (this.selectedCallKey() && this.selectedCallKey() === selectionBefore) {
            this.fetchCallDetail(false);
          }

          this.isListLoading.set(false);
          this.isListRefreshing.set(false);
        },
        error: () => {
          this.listError.set('Failed to load ongoing calls. Please try again.');
          this.isListLoading.set(false);
          this.isListRefreshing.set(false);
        },
      });
  }

  private syncSelectionAfterListUpdate(calls: OngoingCallListItem[]): void {
    const currentKey = this.selectedCallKey();
    if (calls.length === 0) {
      this.selectedCallKey.set(null);
      this.callDetail.set(null);
      this.mobileShowCallDetail.set(false);
      this.chatSocketService.clearVocalTransEngCallRoom();
      return;
    }

    if (currentKey && calls.some((c) => this.callKey(c) === currentKey)) {
      return;
    }

    this.applyCallSelection(calls[0]);
  }

  private fetchCallDetail(isInitial = false): void {
    const call = this.selectedCall();
    if (!call) {
      this.callDetail.set(null);
      return;
    }

    if (isInitial) {
      this.isDetailLoading.set(true);
    }
    this.detailError.set(null);

    const url = `/vocal-trans-eng/ongoing-calls/${call.conversationPid}/${call.traceId}`;

    this.apiService
      .get<unknown>(url, this.buildListParams(), { showLoader: false })
      .subscribe({
        next: (res) => {
          const detail = mapOngoingCallDetail(res);
          if (detail) {
            console.debug('[LiveTranscriptTime] fetchCallDetail mapped transcript', {
              callStartAt: detail.startTime,
              lineCount: detail.transcript.length,
              uniqueTimes: [...new Set(detail.transcript.map((line) => line.formattedTime))],
              preview: detail.transcript.slice(0, 8).map((line, index) => ({
                index,
                formattedTime: line.formattedTime,
                timeSeconds: line.timeSeconds,
                sender: line.sender,
                text: line.text.slice(0, 60),
              })),
            });

            const seeded = seedTranscriptSegments(detail.transcript);
            for (const [key, line] of this.transcriptSegments) {
              if (key.startsWith('received-')) {
                seeded.set(key, line);
              }
            }
            this.transcriptSegments = seeded;
            this.callDetail.set({
              ...detail,
              transcript: transcriptLinesFromSegments(this.transcriptSegments),
            });
          } else {
            this.detailError.set('Unable to parse call details.');
          }
          this.isDetailLoading.set(false);
        },
        error: () => {
          this.detailError.set('Failed to load call details.');
          this.isDetailLoading.set(false);
        },
      });
  }

  private startListPolling(): void {
    this.stopListPolling();
    this.listPollSub = interval(ONGOING_CALLS_REFRESH_MS).subscribe(() => {
      this.fetchOngoingCalls(false);
    });
  }

  private restartListPolling(): void {
    this.startListPolling();
  }

  private stopListPolling(): void {
    this.listPollSub?.unsubscribe();
    this.listPollSub = null;
  }

  private startDurationTimer(): void {
    this.durationSub = interval(1000).subscribe(() => {
      this.durationTick.update((v) => v + 1);
    });
  }

  private setupSocketListeners(): void {
    this.chatSocketService.attachVocalTransEngListeners({
      onCallStreamStart: (data) => this.handleCallStreamStart(data),
      onTranscriptReceived: (data) => this.handleTranscriptReceived(data),
      onCallEnd: (data) => this.handleCallEnd(data),
      onTranscriptSummary: (data) => this.handleTranscriptSummary(data),
      onError: (data) => this.handleSocketError(data),
    });
    this.chatSocketService.connect();
    this.chatSocketService.joinVocalTransEngMonitoring(this.productPid);
  }

  private handleCallStreamStart(data: unknown): void {
    this.fetchOngoingCalls(false);
    this.mergeDetailFromSocket(data);
  }

  private handleTranscriptReceived(data: unknown): void {
    const call = this.selectedCall();
    if (!call) {
      console.debug('[LiveMonitoring] transcript skipped: no selected call', data);
      return;
    }

    const socketIds = extractSocketCallIds(data);
    if (!matchesOngoingCall(data, call)) {
      console.debug('[LiveMonitoring] transcript skipped: call mismatch', {
        socketIds,
        selectedCall: {
          conversationPid: call.conversationPid,
          traceId: call.traceId,
          conversationId: call.conversationId,
        },
        payload: data,
      });
      return;
    }

    const agentName = this.callDetail()?.agentName ?? call.agentName;
    const businessName = this.callDetail()?.businessName ?? call.businessName;
    const callStartAt = this.callDetail()?.startTime ?? call.startTime;
    const segmentsBefore = this.transcriptSegments.size;

    const updated = applySocketTranscriptUpdate(
      this.transcriptSegments,
      data,
      agentName,
      businessName,
      callStartAt
    );

    if (!updated) {
      const mapped = mapSocketTranscriptLine(data, agentName, businessName);
      if (!mapped) {
        console.debug('[LiveMonitoring] transcript skipped: payload not mapped', {
          socketIds,
          payload: data,
        });
        return;
      }
      this.transcriptSegments.set(mapped.segmentKey, mapped.line);
      console.debug('[LiveMonitoring] transcript fallback line mapped', {
        segmentKey: mapped.segmentKey,
        textPreview: mapped.line.text.slice(0, 80),
      });
    }

    console.debug('[LiveMonitoring] transcript applied', {
      socketIds,
      segmentsBefore,
      segmentsAfter: this.transcriptSegments.size,
      segmentKeys: [...this.transcriptSegments.keys()],
    });

    this.applyTranscriptToDetail(call);
  }

  private applyTranscriptToDetail(call: OngoingCallListItem): void {
    const transcript = transcriptLinesFromSegments(this.transcriptSegments);
    const detail = this.callDetail();

    console.debug('[LiveMonitoring] updating call detail transcript', {
      lineCount: transcript.length,
      hasDetail: !!detail,
      preview: transcript.slice(0, 3).map((line) => ({
        sender: line.sender,
        time: line.formattedTime,
        text: line.text.slice(0, 60),
      })),
    });

    if (detail) {
      this.callDetail.set({ ...detail, transcript });
      return;
    }

    this.callDetail.set({
      businessName: call.businessName,
      phoneNumber: call.phoneNumber,
      agentName: call.agentName,
      sentiment: call.sentiment,
      platform: call.platform,
      productName: call.productName,
      startTime: call.startTime,
      transcript,
      cheatSheet: [],
      salesPitch: [],
      summaryLogView: null,
      objectionHandlers: [],
    });
    this.isDetailLoading.set(false);
  }

  private handleTranscriptSummary(data: unknown): void {
    const call = this.selectedCall();
    if (!call || !matchesOngoingCall(data, call)) return;

    const summaryLogView = mapSocketSummaryUpdate(data);
    if (!summaryLogView) return;

    const detail = this.callDetail();
    if (!detail) return;

    this.callDetail.set({
      ...detail,
      summaryLogView,
    });
  }

  private handleCallEnd(data: unknown): void {
    const endedCall = this.ongoingCalls().find((call) => matchesOngoingCall(data, call));
    if (!endedCall) return;

    if (this.selectedCallKey() === this.callKey(endedCall)) {
      this.selectedCallKey.set(null);
      this.callDetail.set(null);
      this.transcriptSegments.clear();
      this.mobileShowCallDetail.set(false);
      this.chatSocketService.clearVocalTransEngCallRoom();
    }

    this.fetchOngoingCalls(false);
  }

  private handleSocketError(data: unknown): void {
    const call = this.selectedCall();
    if (!call || !matchesOngoingCall(data, call)) return;

    const root = unwrapSocketPayload(data);
    const message =
      pickString(root ?? {}, ['message', 'error', 'errorMessage']) ||
      'Live stream error. Please refresh.';
    this.detailError.set(message);
  }

  private mergeDetailFromSocket(data: unknown): void {
    const call = this.selectedCall();
    if (!call || !matchesOngoingCall(data, call)) return;

    const detail = this.callDetail();
    if (!detail) return;

    const root = unwrapSocketPayload(data);
    if (!root) return;

    this.callDetail.set({
      ...detail,
      businessName:
        pickString(root, ['customerBusinessName', 'businessName', 'customerName']) ||
        detail.businessName,
      phoneNumber:
        pickString(root, ['customerPhoneNumber', 'phoneNumber', 'phone']) || detail.phoneNumber,
      agentName: pickString(root, ['agentName', 'agent_name']) || detail.agentName,
      platform: pickString(root, ['platform', 'platformType']) || detail.platform,
      productName: pickString(root, ['productName', 'productKeyname']) || detail.productName,
      startTime:
        pickString(root, ['callStartAt', 'conversationStartTime', 'callStartTime']) ||
        detail.startTime,
      sentiment: pickString(root, ['sentiment', 'callSentiment']) || detail.sentiment,
    });
  }
}
